import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { Router } from '@angular/router';
import {
  PROFILE_TAB_NOTIFICATIONS,
  PROFILE_TAB_QUERY_PARAM
} from '../../../core/components/user-profile/profile-tabs';
import { NotificationSegment } from '../../shared/services/notification/notification-list.store';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { groupByDay } from './notification-groups';

// Die Datumskoepfe aelterer Gruppen zeigen den Wochentag. Die App setzt keinen globalen
// LOCALE_ID (nur tour-planning.module tut das fuer sich), Angulars date-Pipe faellt sonst auf
// 'en-US' zurueck und schriebe "Wednesday". Muster: announcement-body-input.component.ts —
// registerLocaleData macht die 'de'-Daten verfuegbar, ohne den globalen Provider zu setzen und
// damit Zahlen- und Datumsformate der ganzen App umzustellen.
registerLocaleData(localeDe, 'de');

/**
 * Popover-Inhalt der Glocke (#736). Wird von p-overlayPanel pro Oeffnen NEU instanziiert
 * (*ngIf="render" wird bei jedem Schliessen zurueckgesetzt) — der Listenzustand liegt deshalb im
 * Store des NotificationService, nicht hier.
 *
 * UI-Ueberarbeitung 19.08.2026 (Vorbild: Benachrichtigungs-Screen der Mobile-App): Der Auszug mit
 * fuenf Zeilen und dem „mehr…"-Knopf ist einem endlos scrollenden, nach Tagen gruppierten Stream
 * gewichen. Kopf und Filterzeile stehen fest, nur die Liste scrollt.
 */
@Component({
  selector: "app-notification-center",
  templateUrl: "notification-center.html"
})
export class NotificationCenterComponent implements OnInit, OnDestroy {

  readonly list = this.notificationService.popoverList;
  readonly unreadCount = this.notificationService.unreadCount;

  /** Tagesgruppen des Streams — „Heute", „Gestern", dann datiert. Siehe notification-groups.ts. */
  readonly groups = computed(() => groupByDay(this.list.items()));

  /**
   * Client-Dok §2 kennt drei Leerzustaende; bei zwei Segmenten sind zwei davon sichtbar.
   * Leer im Segment "Alle" heisst: noch nie eine Benachrichtigung bekommen.
   * Leer im Segment "Ungelesen" heisst: alles gelesen — der Normalfall, positiv formuliert.
   * Der dritte Fall (die TTL hat geraeumt) verlangt laut Dok ausdruecklich nichts Besonderes.
   */
  readonly emptyStateKey = computed(() =>
    this.list.segment() === 'all'
      ? 'notificationCenterComponent.emptyStateFirstTime'
      : 'notificationCenterComponent.emptyState'
  );

  /**
   * Feuert, wenn aus dem Popover heraus navigiert wurde — aus einer Zeile oder ueber das Zahnrad.
   * Das Layout haengt daran und schliesst das Overlay.
   */
  @Output() navigated = new EventEmitter<void>();

  /** Die scrollende Flaeche. Zugleich `root` des Observers — nicht der Viewport. */
  @ViewChild('scrollBox') private scrollBox?: ElementRef<HTMLElement>;

  private observer: IntersectionObserver | null = null;

  constructor(
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.list.loadFirstPage();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  /**
   * Infinite Scroll (Nacharbeit 19.08.2026, ersetzt Entscheidung 10 — kein „mehr…" mehr).
   *
   * Ein Setter und kein `ngAfterViewInit`: der Sentinel haengt an `*ngIf` — er verschwindet bei
   * jedem Fehler, bei jedem Erstladen und am Ende der Liste und kommt danach als NEUER Knoten
   * zurueck. Ein einmalig in ngAfterViewInit aufgesetzter Observer beobachtete danach eine Leiche.
   *
   * `rootMargin` laedt ein Stueck im Voraus, damit der Nutzer die Naht nicht sieht.
   *
   * Die Eigenheit „leere Seite, aber Cursor gesetzt" (Client-Dok §2) faengt der Store ab, nicht
   * diese Komponente: ohne neue Zeilen bewegt sich der Sentinel nicht, der Observer feuert also
   * kein zweites Mal — und ein Nachfassen von hier aus muesste raten, wann es genug ist.
   */
  @ViewChild('sentinel') set sentinel(ref: ElementRef<HTMLElement> | undefined) {
    this.observer?.disconnect();
    this.observer = null;

    if (!ref || typeof IntersectionObserver === 'undefined') {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.list.loadMore();
        }
      },
      { root: this.scrollBox?.nativeElement ?? null, rootMargin: '300px' }
    );
    this.observer.observe(ref.nativeElement);
  }

  /** Das Zahnrad fuehrt auf die Benachrichtigungs-Einstellungen (#735). */
  goToSettings(): void {
    this.router
      .navigate(['/profile'], {
        queryParams: { [PROFILE_TAB_QUERY_PARAM]: PROFILE_TAB_NOTIFICATIONS }
      })
      .catch((error) => console.warn('notification-center: Navigation abgewiesen', error));
    this.navigated.emit();
  }

  /**
   * Ein Klick auf das bereits aktive Segment tut nichts. Frueher verhinderte das ein `disabled` am
   * Knopf — das dimmte ihn aber und musste per ::ng-deep wieder entdimmt werden. Der Riegel gehoert
   * hierher, nicht in eine Stilregel.
   */
  setSegment(segment: NotificationSegment): void {
    if (segment === this.list.segment()) {
      return;
    }
    this.list.setSegment(segment);
  }

  markAllAsRead(): void {
    this.list.markAllRead();
  }

}
